import type { InputHTMLAttributes } from "react";
import { forwardRef } from "react";
import { Input as ReactAriaInput } from "react-aria-components";

import { cn, cv, type VariantProps } from "../utils";

export const ncInputWidthVariants = cv("", {
  variants: {
    inputWidth: {
      auto: "w-auto",
      xsmall: "w-[5.5rem]",
      small: "w-40",
      medium: "w-60",
      large: "w-full max-w-[25rem]",
      full: "w-full max-w-full",
    },
  },
  defaultVariants: {
    inputWidth: "auto",
  },
});

export const ncInputClasses = cn(
  // Base styles
  "bg-light border-ui max-w-full rounded-md border px-3 py-2 font-normal transition-colors duration-300 outline-main",
  // Focus
  "data-focused:border-active data-focused:outline-main data-focused:outline-2 data-focused:outline-offset-2",
  // Placeholder
  "placeholder-ui-dark",
  // Disabled
  "data-disabled:bg-disabled-light data-disabled:cursor-not-allowed disabled:bg-disabled-light disabled:cursor-not-allowed",
  // Invalid
  "data-invalid:border-danger"
);

export interface NcInputProps
  extends InputHTMLAttributes<HTMLInputElement>, VariantProps<typeof ncInputWidthVariants> {
  type?: "text" | "password" | "email" | "number" | "file" | "tel" | "url" | "search";
}

export const NcInput = forwardRef<HTMLInputElement, NcInputProps>(
  (
    { autoComplete = "off", autoCapitalize = "none", inputWidth = "auto", className, ...props },
    ref
  ) => {
    return (
      <ReactAriaInput
        data-nc="NcInput"
        ref={ref}
        className={cn(
          ncInputClasses,
          // File input
          props.type === "file" &&
            "file:text-main file:border-ui file:hover:bg-main file:hover:text-light cursor-pointer file:mr-3 file:cursor-pointer file:rounded-sm file:border file:border-solid file:px-2 file:py-1 file:text-xs file:font-bold file:transition-all file:duration-300",
          ncInputWidthVariants({ inputWidth }),
          className
        )}
        autoComplete={autoComplete}
        autoCapitalize={autoCapitalize}
        maxLength={props.maxLength || 255}
        data-1p-ignore
        {...props}
      />
    );
  }
);
